// Feature-flag-aware twin of `dynamicWithExperiment` for dual-mode routes.
// Do NOT use `dynamicWithExperiment` here — it checks `=== 'treatment'`, but
// feature flags speak `'on'`/`'off'`, so the SSR entry would never load.
import { type FC } from 'react'
import { type FeatureFlag } from '.'
import { useHydratedFeatureFlag } from './hooks'

type SyncEntryLoader = () => JSX.Element

interface DynamicWithFeatureFlagOptions {
  flagName: FeatureFlag
  /** Rendered when the flag is `'on'`. Must be a top-level import (no lazy boundary). */
  on: SyncEntryLoader
  /** Rendered when the flag is `'off'` (default). Same semantics as `on`. */
  off: SyncEntryLoader
}

/**
 * Returns a component that synchronously picks between `on` and `off` entries
 * using `useHydratedFeatureFlag`, keeping SSR and hydration in agreement.
 *
 * Both entries must be top-level imports so SSR emits the body inline.
 *
 * @example
 * export default dynamicWithFeatureFlag({
 *   flagName: FeatureFlags.SsrDualMode,
 *   off: () => <CSREntry />,
 *   on: () => <FullSSREntry />,
 * })
 */
export function dynamicWithFeatureFlag(
  options: DynamicWithFeatureFlagOptions,
): FC {
  const { flagName, on, off } = options
  return function DynamicWithFeatureFlag() {
    const flagOn = useHydratedFeatureFlag(flagName)
    return flagOn ? on() : off()
  }
}
